/**
 * MoM detail page — view + edit a single meeting, manage decisions/action items.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../lib/api';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import {
  ArrowLeft, Calendar, MapPin, Users, User, Edit3, Save, X, Plus, Trash2,
  Clock, CheckCircle2, AlertTriangle, Ban, Printer, Download, Tag, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (s) => {
  if (!s) return '';
  try { return new Date(s + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const statusMeta = {
  open:        { label: 'Open',         color: 'bg-slate-100 text-slate-700 border-slate-200',       icon: Clock },
  in_progress: { label: 'In Progress',  color: 'bg-sky-50 text-sky-700 border-sky-200',              icon: Clock },
  done:        { label: 'Completed',    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',  icon: CheckCircle2 },
  blocked:     { label: 'Blocked',      color: 'bg-rose-50 text-rose-700 border-rose-200',           icon: Ban },
};
const priorityMeta = {
  high:   { label: 'High',   color: 'bg-red-50 text-red-700 border-red-200' },
  medium: { label: 'Medium', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  low:    { label: 'Low',    color: 'bg-slate-100 text-slate-600 border-slate-200' },
};
const typeColor = {
  'Management Review': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Weekly Sync': 'bg-sky-50 text-sky-700 border-sky-200',
  'Strategy': 'bg-rose-50 text-rose-700 border-rose-200',
  'Operations': 'bg-amber-50 text-amber-700 border-amber-200',
  'HR Review': 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  'Finance Review': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Training Review': 'bg-blue-50 text-blue-700 border-blue-200',
  'Ad-hoc': 'bg-slate-100 text-slate-700 border-slate-200',
};

const today = () => new Date().toISOString().slice(0, 10);

export default function MomDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editHeader, setEditHeader] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({});
  const [types, setTypes] = useState([]);
  const [decisionModal, setDecisionModal] = useState({ open: false, mode: 'add', data: null });
  const [people, setPeople] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/mom/meetings/${id}`);
      setMeeting(r.data);
    } catch (e) {
      toast.error('Meeting not found');
      nav('/training/mom');
    } finally { setLoading(false); }
  }, [id, nav]);

  useEffect(() => {
    load();
    api.get('/mom/meta').then(r => setTypes(r.data.meeting_types || [])).catch(() => {});
    api.get('/mom/people').then(r => setPeople(r.data.people || [])).catch(() => {});
  }, [load]);

  const startEditHeader = () => {
    setHeaderDraft({
      title: meeting.title,
      meeting_date: meeting.meeting_date,
      meeting_time: meeting.meeting_time || '',
      location: meeting.location || '',
      meeting_type: meeting.meeting_type,
      chairperson: meeting.chairperson || '',
      attendees: (meeting.attendees || []).join(', '),
      agenda: meeting.agenda || '',
      summary: meeting.summary || '',
      tags: (meeting.tags || []).join(', '),
    });
    setEditHeader(true);
  };

  const saveHeader = async () => {
    const payload = {
      ...headerDraft,
      attendees: headerDraft.attendees.split(',').map(s => s.trim()).filter(Boolean),
      tags: headerDraft.tags.split(',').map(s => s.trim()).filter(Boolean),
    };
    try {
      const r = await api.put(`/mom/meetings/${id}`, payload);
      setMeeting(r.data);
      setEditHeader(false);
      toast.success('Meeting updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const onDeleteMeeting = async () => {
    if (!window.confirm('Delete this meeting? This cannot be undone.')) return;
    try {
      await api.delete(`/mom/meetings/${id}`);
      toast.success('Meeting deleted');
      nav('/training/mom');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const onPrint = () => window.print();

  const onExport = () => {
    if (!meeting) return;
    const lines = [];
    lines.push(`MINUTES OF MEETING`);
    lines.push(`=====================================`);
    lines.push(`Title       : ${meeting.title}`);
    lines.push(`Date        : ${fmtDate(meeting.meeting_date)}${meeting.meeting_time ? ' · ' + meeting.meeting_time : ''}`);
    lines.push(`Type        : ${meeting.meeting_type}`);
    lines.push(`Location    : ${meeting.location || '-'}`);
    lines.push(`Chairperson : ${meeting.chairperson || '-'}`);
    lines.push(`Attendees   : ${(meeting.attendees || []).join(', ') || '-'}`);
    lines.push('');
    lines.push(`AGENDA`);
    lines.push(`-------------------------------------`);
    lines.push(meeting.agenda || '-');
    lines.push('');
    lines.push(`SUMMARY`);
    lines.push(`-------------------------------------`);
    lines.push(meeting.summary || '-');
    lines.push('');
    lines.push(`DECISIONS & ACTION ITEMS`);
    lines.push(`-------------------------------------`);
    (meeting.decisions || []).forEach((d, i) => {
      lines.push(`${i + 1}. ${d.decision}`);
      lines.push(`   Responsible : ${d.responsible_person || '-'}`);
      lines.push(`   Due         : ${d.due_date ? fmtDate(d.due_date) : '-'}`);
      lines.push(`   Priority    : ${d.priority}`);
      lines.push(`   Status      : ${statusMeta[d.status]?.label || d.status}`);
      if (d.notes) lines.push(`   Notes       : ${d.notes}`);
      lines.push('');
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `MoM_${meeting.meeting_date || 'undated'}_${meeting.title.replace(/\W+/g, '_').slice(0, 40)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const updateDecisionStatus = async (decision, status) => {
    try {
      const r = await api.put(`/mom/meetings/${id}/decisions/${decision.id}`, { status });
      setMeeting(m => ({
        ...m,
        decisions: m.decisions.map(d => d.id === decision.id ? r.data : d),
      }));
    } catch (e) { toast.error('Update failed'); }
  };

  const deleteDecision = async (decision) => {
    if (!window.confirm('Delete this action item?')) return;
    try {
      await api.delete(`/mom/meetings/${id}/decisions/${decision.id}`);
      setMeeting(m => ({ ...m, decisions: m.decisions.filter(d => d.id !== decision.id) }));
      toast.success('Deleted');
    } catch (e) { toast.error('Delete failed'); }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!meeting) return null;

  const decisions = meeting.decisions || [];
  const counts = {
    total: decisions.length,
    open: decisions.filter(d => d.status === 'open').length,
    in_progress: decisions.filter(d => d.status === 'in_progress').length,
    done: decisions.filter(d => d.status === 'done').length,
    blocked: decisions.filter(d => d.status === 'blocked').length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 pb-16 print:bg-white">
      <div className="max-w-[1100px] mx-auto px-6 pt-6">
        {/* Back + actions bar */}
        <div className="flex items-center justify-between mb-4 print:hidden">
          <Button variant="ghost" size="sm" onClick={() => nav('/training/mom')} data-testid="mom-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to all meetings
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onPrint} data-testid="mom-print-btn">
              <Printer className="w-4 h-4 mr-1.5" /> Print
            </Button>
            <Button variant="outline" size="sm" onClick={onExport} data-testid="mom-export-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export
            </Button>
            {!editHeader ? (
              <Button variant="outline" size="sm" onClick={startEditHeader} data-testid="mom-edit-btn">
                <Edit3 className="w-4 h-4 mr-1.5" /> Edit
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => setEditHeader(false)}>
                  <X className="w-4 h-4 mr-1.5" /> Cancel
                </Button>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={saveHeader} data-testid="mom-save-btn">
                  <Save className="w-4 h-4 mr-1.5" /> Save
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={onDeleteMeeting} className="text-rose-600 border-rose-200 hover:bg-rose-50">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Header card */}
        <Card className="p-6 mb-5 border-slate-200 shadow-sm bg-white">
          {!editHeader ? (
            <>
              <div className="flex items-start gap-3 flex-wrap mb-3">
                <Badge variant="outline" className={`text-[10px] font-semibold ${typeColor[meeting.meeting_type] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                  {meeting.meeting_type}
                </Badge>
                {(meeting.tags || []).map(t => (
                  <Badge key={t} variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
                    <Tag className="w-2.5 h-2.5 mr-1" />{t}
                  </Badge>
                ))}
              </div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2" data-testid="mom-title">{meeting.title}</h1>
              <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-4">
                <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-slate-400" />{fmtDate(meeting.meeting_date)}{meeting.meeting_time ? ` · ${meeting.meeting_time}` : ''}</span>
                {meeting.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" />{meeting.location}</span>}
                {meeting.chairperson && <span className="flex items-center gap-1.5"><User className="w-4 h-4 text-slate-400" />Chair: <strong className="text-slate-800">{meeting.chairperson}</strong></span>}
              </div>
              {meeting.attendees?.length > 0 && (
                <div className="flex items-start gap-2 text-sm mb-4">
                  <Users className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div className="flex flex-wrap gap-1.5">
                    {meeting.attendees.map(a => (
                      <Badge key={a} variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200">{a}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {meeting.agenda && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Agenda</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{meeting.agenda}</p>
                </div>
              )}
              {meeting.summary && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Summary / Key Notes</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{meeting.summary}</p>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={headerDraft.title} onChange={e => setHeaderDraft({ ...headerDraft, title: e.target.value })} data-testid="mom-edit-title" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" value={headerDraft.meeting_date} onChange={e => setHeaderDraft({ ...headerDraft, meeting_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Time</Label>
                  <Input type="time" value={headerDraft.meeting_time} onChange={e => setHeaderDraft({ ...headerDraft, meeting_time: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={headerDraft.meeting_type} onValueChange={v => setHeaderDraft({ ...headerDraft, meeting_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Location</Label>
                  <Input value={headerDraft.location} onChange={e => setHeaderDraft({ ...headerDraft, location: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Chairperson</Label>
                  <Input value={headerDraft.chairperson} onChange={e => setHeaderDraft({ ...headerDraft, chairperson: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Attendees (comma-separated)</Label>
                  <Input value={headerDraft.attendees} onChange={e => setHeaderDraft({ ...headerDraft, attendees: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Agenda</Label>
                <Textarea rows={3} value={headerDraft.agenda} onChange={e => setHeaderDraft({ ...headerDraft, agenda: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Summary</Label>
                <Textarea rows={3} value={headerDraft.summary} onChange={e => setHeaderDraft({ ...headerDraft, summary: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Tags (comma-separated)</Label>
                <Input value={headerDraft.tags} onChange={e => setHeaderDraft({ ...headerDraft, tags: e.target.value })} />
              </div>
            </div>
          )}
        </Card>

        {/* Decisions / Action Items */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-indigo-600" />
              Decisions & Action Items
              <Badge variant="outline" className="text-xs bg-slate-50 text-slate-700 border-slate-200">{counts.total}</Badge>
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {counts.open + counts.in_progress} open · {counts.done} done · {counts.blocked} blocked
            </p>
          </div>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 print:hidden" onClick={() => setDecisionModal({ open: true, mode: 'add', data: { decision: '', responsible_person: '', due_date: '', status: 'open', priority: 'medium', notes: '' } })} data-testid="mom-add-decision-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add action item
          </Button>
        </div>

        {decisions.length === 0 ? (
          <Card className="p-10 text-center border-dashed">
            <p className="text-sm text-slate-500">No action items yet. Click “Add action item” to capture one.</p>
          </Card>
        ) : (
          <div className="space-y-2.5">
            {decisions.map((d, idx) => {
              const st = statusMeta[d.status] || statusMeta.open;
              const pr = priorityMeta[d.priority] || priorityMeta.medium;
              const overdue = d.due_date && d.due_date < today() && d.status !== 'done';
              const StIcon = st.icon;
              return (
                <Card key={d.id} className={`p-4 bg-white border-slate-200 hover:shadow-sm transition-shadow ${overdue ? 'border-l-4 border-l-rose-500' : ''}`} data-testid={`mom-decision-${d.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 leading-relaxed">{d.decision}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                          <User className="w-2.5 h-2.5 mr-1" />{d.responsible_person || 'Unassigned'}
                        </Badge>
                        {d.due_date && (
                          <Badge variant="outline" className={`text-[10px] ${overdue ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                            <Calendar className="w-2.5 h-2.5 mr-1" />Due {fmtDate(d.due_date)}{overdue ? ' · overdue' : ''}
                          </Badge>
                        )}
                        <Badge variant="outline" className={`text-[10px] ${pr.color}`}>{pr.label}</Badge>
                        <Badge variant="outline" className={`text-[10px] ${st.color}`}>
                          <StIcon className="w-2.5 h-2.5 mr-1" />{st.label}
                        </Badge>
                      </div>
                      {d.notes && <p className="text-xs text-slate-600 mt-2 italic">“{d.notes}”</p>}
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0 print:hidden">
                      <Select value={d.status} onValueChange={(v) => updateDecisionStatus(d, v)}>
                        <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`mom-decision-status-${d.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="done">Completed</SelectItem>
                          <SelectItem value="blocked">Blocked</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDecisionModal({ open: true, mode: 'edit', data: { ...d } })}>
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600 hover:bg-rose-50" onClick={() => deleteDecision(d)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <DecisionModal
        open={decisionModal.open}
        mode={decisionModal.mode}
        initial={decisionModal.data}
        people={people}
        onClose={() => setDecisionModal({ open: false, mode: 'add', data: null })}
        onSaved={(saved) => {
          setMeeting(m => {
            const decs = m.decisions || [];
            if (decisionModal.mode === 'edit') {
              return { ...m, decisions: decs.map(d => d.id === saved.id ? saved : d) };
            }
            return { ...m, decisions: [...decs, saved] };
          });
          setDecisionModal({ open: false, mode: 'add', data: null });
        }}
        meetingId={id}
      />
    </div>
  );
}

function DecisionModal({ open, mode, initial, people, onClose, onSaved, meetingId }) {
  const [draft, setDraft] = useState(initial);
  useEffect(() => { setDraft(initial); }, [initial]);
  if (!draft) return null;

  const save = async () => {
    if (!draft.decision?.trim()) { toast.error('Decision is required'); return; }
    if (!draft.responsible_person?.trim()) { toast.error('Responsible person is required'); return; }
    try {
      const payload = {
        decision: draft.decision.trim(),
        responsible_person: draft.responsible_person.trim(),
        due_date: draft.due_date || null,
        status: draft.status || 'open',
        priority: draft.priority || 'medium',
        notes: draft.notes || '',
      };
      if (mode === 'edit') {
        const r = await api.put(`/mom/meetings/${meetingId}/decisions/${draft.id}`, payload);
        onSaved(r.data);
        toast.success('Updated');
      } else {
        const r = await api.post(`/mom/meetings/${meetingId}/decisions`, payload);
        onSaved(r.data);
        toast.success('Added');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit action item' : 'Add action item'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Decision / Action</Label>
            <Textarea rows={3} value={draft.decision} onChange={e => setDraft({ ...draft, decision: e.target.value })} placeholder="What needs to be done?" data-testid="mom-decision-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Responsible person *</Label>
              <Input list="mom-people" value={draft.responsible_person} onChange={e => setDraft({ ...draft, responsible_person: e.target.value })} placeholder="e.g., Anupam" data-testid="mom-responsible-input" />
              <datalist id="mom-people">
                {people.map(p => <option key={p} value={p} />)}
              </datalist>
            </div>
            <div>
              <Label className="text-xs">Due date</Label>
              <Input type="date" value={draft.due_date || ''} onChange={e => setDraft({ ...draft, due_date: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={draft.priority} onValueChange={v => setDraft({ ...draft, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={draft.status} onValueChange={v => setDraft({ ...draft, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="done">Completed</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={draft.notes || ''} onChange={e => setDraft({ ...draft, notes: e.target.value })} placeholder="Optional context" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={save} data-testid="mom-decision-save">
            {mode === 'edit' ? 'Save changes' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
